<script setup lang="ts">
import { useAuthStore } from '@libero/hooks/store/useAuthStore';
import { useForm } from '@libero/hooks/useForm';
import Alert from '@libero/ui-framework/Alert/Alert.vue';
import Button from '@libero/ui-framework/Button/Button.vue';
import Checkbox from '@libero/ui-framework/Checkbox/Checkbox.vue';
import Form from '@libero/ui-framework/Form/Form.vue';
import FormLayout from '@libero/ui-framework/FormLayout/FormLayout.vue';
import HeightMotion from '@libero/ui-framework/HeightMotion/HeightMotion.vue';
import Input from '@libero/ui-framework/Input/Input.vue';
import InputGroup from '@libero/ui-framework/InputGroup/InputGroup.vue';
import Label from '@libero/ui-framework/Label/Label.vue';
import Link from '@libero/ui-framework/Link/Link.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import { useMutation } from '@tanstack/vue-query';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const form = useForm({
  email: '',
  password: '',
  remember: false,
});

const { mutateAsync, isPending } = useMutation({
  mutationFn: authStore.login,
  onSuccess: (data) => {
    if (data && !data.two_factor_enabled_at) {
      authStore.redirect();
    } else {
      router.replace({
        name: 'auth.verify-two-factor',
        state: { email: form.values.email },
      });
    }
  },
});

const handleSubmit = () => {
  form.submit(mutateAsync);
};

const isVerified = computed(() => route.query.verified === '1');
const isChanged = computed(() => route.query.changed === '1');
</script>

<template>
  <Form v-bind="form.registerForm(handleSubmit)">
    <HeightMotion>
      <FormLayout v-if="isVerified">
        <Alert type="success">
          {{ t('auth.verify-email-success') }}
        </Alert>
      </FormLayout>
    </HeightMotion>

    <HeightMotion>
      <FormLayout v-if="isChanged">
        <Alert type="success">
          {{ t('auth.reset-password-success') }}
        </Alert>
      </FormLayout>
    </HeightMotion>

    <FormLayout>
      <InputGroup>
        <Label size="lg" required for="email" :value="t('user.email')" />
        <Input
          size="lg"
          type="email"
          :placeholder="t('user.email-placeholder')"
          v-bind="form.register('email')"
        />
      </InputGroup>

      <Stack :gap="0.25">
        <InputGroup>
          <Label size="lg" required for="password" :value="t('user.password')" />

          <Input
            size="lg"
            type="password"
            autocomplete="current-password"
            v-bind="form.register('password')"
          />
        </InputGroup>

        <Link size="sm" underline :to="{ name: 'auth.forgot-password' }">
          {{ t('auth.forgot') }}?
        </Link>
      </Stack>

      <InputGroup>
        <Checkbox v-bind="form.register('remember')">
          {{ t('auth.remember') }}
        </Checkbox>
      </InputGroup>

      <Button type="submit" size="lg" isFullWidth isCentered :isLoading="isPending">
        <template #label>{{ t('auth.login') }}</template>
      </Button>
    </FormLayout>
  </Form>
</template>
